import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { logout, verificarSessao } from '@/lib/auth'
import { bff, BffError } from '@/lib/bff'
import '../styles/AcessoRestrito.css'

interface Arquivo {
  id: string
  nome: string
  descricao: string
  categoria: string
  extensao: string
  url: string
}

const ICONE_EXTENSAO: Record<string, string> = {
  '7z':  'fa-file-zipper',
  'rar': 'fa-file-zipper',
  'zip': 'fa-file-zipper',
  'exe': 'fa-file-code',
  'sh':  'fa-terminal',
  'url': 'fa-arrow-up-right-from-square',
}

function iconeExt(ext: string) {
  return ICONE_EXTENSAO[ext] ?? 'fa-file'
}

function catSlug(categoria: string): string {
  return categoria
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-')
}

export default function AcessoRestrito() {
  const navigate = useNavigate()
  const [arquivos, setArquivos]                   = useState<Arquivo[]>([])
  const [carregando, setCarregando]               = useState(true)
  const [erro, setErro]                           = useState('')
  const [baixando, setBaixando]                   = useState<string | null>(null)
  const [copiado, setCopiado]                     = useState<string | null>(null)
  const [categoriaSelecionada, setCategoriaSelecionada] = useState('Todos')
  const [busca, setBusca]                         = useState('')

  useEffect(() => {
    // Redireciona para login se não houver sessão ativa
    if (!verificarSessao()) {
      navigate('/login', { replace: true })
      return
    }

    bff.get<Arquivo[]>('/v1/downloads')
      .then(setArquivos)
      .catch(err => {
        if (err instanceof BffError && err.status === 401) {
          navigate('/login', { replace: true })
        } else {
          setErro('Erro ao carregar a lista de arquivos.')
        }
      })
      .finally(() => setCarregando(false))
  }, [navigate])

  function handleLogout() {
    logout()
    navigate('/login', { replace: true })
  }

  function handleDownload(arquivo: Arquivo) {
    setBaixando(arquivo.id)
    window.open(arquivo.url, '_blank', 'noopener,noreferrer')
    setTimeout(() => setBaixando(null), 2000)
  }

  async function handleCopiarLink(arquivo: Arquivo) {
    const link = new URL(arquivo.url, window.location.origin).href
    try {
      await navigator.clipboard.writeText(link)
    } catch {
      // Fallback para navegadores sem Clipboard API (ex.: página servida sem HTTPS)
      const textarea = document.createElement('textarea')
      textarea.value = link
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
    setCopiado(arquivo.id)
    setTimeout(() => setCopiado(null), 2000)
  }

  const categorias = ['Todos', ...Array.from(new Set(arquivos.map(a => a.categoria)))]

  const arquivosFiltrados = arquivos.filter(a => {
    const naCategoria = categoriaSelecionada === 'Todos' || a.categoria === categoriaSelecionada
    const naBusca = busca === '' ||
      a.nome.toLowerCase().includes(busca.toLowerCase()) ||
      a.descricao.toLowerCase().includes(busca.toLowerCase())
    return naCategoria && naBusca
  })

  return (
    <div className="ar-page">

      {/* ══ HEADER ══ */}
      <header className="ar-header">
        <div className="ar-header-inner">
          <Link to="/" className="ar-header-logo">
            <img src="/img/geral/logo-quantum-branca.png" alt="Sistema Quantum" />
          </Link>
          <div className="ar-header-right">
            <span className="ar-area-badge">
              <i className="fa-solid fa-shield-halved" />
              Área Restrita
            </span>
            <button className="ar-logout-btn" onClick={handleLogout}>
              <i className="fa-solid fa-right-from-bracket" />
              Sair
            </button>
          </div>
        </div>
      </header>

      {/* ══ MAIN ══ */}
      <main className="ar-main">

        {/* Topo */}
        <div className="ar-top">
          <div className="ar-top-eyebrow">Downloads</div>
          <h1 className="ar-top-title">Sistema Quantum — Arquivos Exclusivos</h1>
          <p className="ar-top-sub">
            Disponível apenas para clientes com contrato ativo. Mantenha sempre a versão mais recente instalada.
          </p>
        </div>

        {/* Erro */}
        {erro && (
          <div className="ar-alert-error">
            <i className="fa-solid fa-circle-exclamation" />
            <span>{erro}</span>
          </div>
        )}

        {/* Loading */}
        {carregando && (
          <div className="ar-loading">
            <i className="fa-solid fa-circle-notch fa-spin" />
            Carregando arquivos...
          </div>
        )}

        {/* Conteúdo principal */}
        {!carregando && arquivos.length > 0 && (
          <>
            {/* Toolbar */}
            <div className="ar-toolbar">
              <div className="ar-search-wrap">
                <i className="fa-solid fa-magnifying-glass" />
                <input
                  className="ar-search-input"
                  type="text"
                  placeholder="Buscar arquivo..."
                  value={busca}
                  onChange={e => setBusca(e.target.value)}
                />
              </div>
              <div className="ar-filters">
                {categorias.map(cat => (
                  <button
                    key={cat}
                    className={`ar-filter-btn${categoriaSelecionada === cat ? ' active' : ''}`}
                    onClick={() => setCategoriaSelecionada(cat)}
                  >
                    {cat}
                    {cat !== 'Todos' && (
                      <span className="ar-filter-count">
                        {arquivos.filter(a => a.categoria === cat).length}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Contador */}
            <p className="ar-count">
              {arquivosFiltrados.length} {arquivosFiltrados.length === 1 ? 'arquivo' : 'arquivos'} encontrado{arquivosFiltrados.length !== 1 ? 's' : ''}
            </p>

            {/* Grid de cards */}
            {arquivosFiltrados.length > 0 ? (
              <div className="ar-grid">
                {arquivosFiltrados.map(arq => {
                  const slug = catSlug(arq.categoria)
                  return (
                    <div key={arq.id} className="ar-card">
                      <div className="ar-card-top">
                        <div className={`ar-card-icon-wrap ar-icon-${slug}`}>
                          <i className={`fa-solid ${iconeExt(arq.extensao)}`} />
                        </div>
                        <div className="ar-card-info">
                          <div className="ar-card-name">{arq.nome}</div>
                          <div className="ar-card-ext">.{arq.extensao}</div>
                        </div>
                      </div>

                      <p className="ar-card-desc">{arq.descricao}</p>

                      <div className="ar-card-footer">
                        <span className={`ar-cat-badge ar-cat-${slug}`}>
                          {arq.categoria}
                        </span>
                        <div className="ar-card-actions">
                          <button
                            className={`ar-copy-btn${copiado === arq.id ? ' copiado' : ''}`}
                            onClick={() => handleCopiarLink(arq)}
                            title={copiado === arq.id ? 'Link copiado!' : 'Copiar link'}
                            aria-label="Copiar link"
                          >
                            <i className={`fa-solid ${copiado === arq.id ? 'fa-check' : 'fa-link'}`} />
                          </button>
                          <button
                            className="ar-download-btn"
                            onClick={() => handleDownload(arq)}
                            disabled={baixando === arq.id}
                          >
                            {baixando === arq.id ? (
                              <><i className="fa-solid fa-circle-notch fa-spin" />Abrindo...</>
                            ) : (
                              <><i className="fa-solid fa-download" />Baixar</>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="ar-empty">
                <i className="fa-solid fa-magnifying-glass" />
                <div className="ar-empty-title">Nenhum arquivo encontrado</div>
                <p className="ar-empty-sub">Tente outros termos ou limpe o filtro de categoria.</p>
              </div>
            )}
          </>
        )}

        {/* Sem arquivos */}
        {!carregando && arquivos.length === 0 && !erro && (
          <div className="ar-empty">
            <i className="fa-solid fa-folder-open" />
            <div className="ar-empty-title">Nenhum arquivo disponível no momento</div>
            <p className="ar-empty-sub">
              Entre em contato com o suporte:{' '}
              <a href="tel:+556332143357">(63) 3214-3357</a>
            </p>
          </div>
        )}

        {/* Banner informativo */}
        <div className="ar-info-banner">
          <i className="fa-solid fa-shield-halved" />
          <span>
            Os arquivos disponibilizados aqui são exclusivos para clientes com contrato ativo.
            Não compartilhe seus dados de acesso. Em caso de dúvidas, fale com o suporte.
          </span>
        </div>

      </main>
    </div>
  )
}
